import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { Award, Lock, Loader2 } from "lucide-react";

export default function Achievements() {
  const [badges, setBadges] = useState([]);
  const [earned, setEarned] = useState([]);
  const [awards, setAwards] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/badges"), api.get("/progress")])
      .then(([b, p]) => {
        setBadges(b.data);
        setEarned(p.data.earned_badges);
        setAwards(p.data.badge_awards || {});
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Award className="h-6 w-6 text-accent" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">Flight Record</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-1">Achievements</h1>
        <p className="text-sm text-muted-foreground mb-10 font-mono">
          {earned.length}/{badges.length} badges unlocked
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {badges.map((b, i) => {
            const Icon = getIcon(b.icon);
            const unlocked = earned.includes(b.id);
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                data-testid={`badge-${b.id}`}
                className={`p-6 rounded-sm border text-center transition-colors ${
                  unlocked ? "border-primary/50 bg-primary/5" : "border-border/60 bg-card opacity-70"
                }`}
              >
                <div className={`h-16 w-16 mx-auto rounded-sm flex items-center justify-center mb-4 ${
                  unlocked ? "bg-primary/15 glow-cyan" : "bg-secondary"
                }`}>
                  {unlocked ? <Icon className="h-8 w-8 text-primary" /> : <Lock className="h-7 w-7 text-muted-foreground" />}
                </div>
                <h3 className="font-display font-bold">{b.name}</h3>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{b.desc}</p>
                <span className={`inline-block mt-3 text-[10px] font-mono uppercase tracking-widest ${unlocked ? "text-primary" : "text-muted-foreground"}`}>
                  {unlocked ? (awards[b.id] ? `Earned ${new Date(awards[b.id]).toLocaleDateString()}` : "Unlocked") : "Locked"}
                </span>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
